import React from "react";
import "./HTML_MAIN_CSS.css";

const HTMLLayout = () => {
  return (
    <div className="html-container">
      <h1>HTML – Layout</h1>

      {/* 1. Introduction */}
      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          HTML layout defines the structural arrangement of content on a web page. Layout elements help organize text, images, and other media in a readable and visually appealing way.
        </p>
      </section>

      {/* 2. Basic Layout Elements */}
      <section id="basic-elements">
        <h2>2. Basic Layout Elements</h2>
        <ul>
          <li><strong>&lt;header&gt;</strong> – Represents the top section or page header.</li>
          <li><strong>&lt;nav&gt;</strong> – Defines navigation links.</li>
          <li><strong>&lt;main&gt;</strong> – Main content of the page.</li>
          <li><strong>&lt;section&gt;</strong> – Thematic grouping of content.</li>
          <li><strong>&lt;article&gt;</strong> – Independent content block (like a blog post).</li>
          <li><strong>&lt;aside&gt;</strong> – Sidebar or related content.</li>
          <li><strong>&lt;footer&gt;</strong> – Footer content, copyright, or related links.</li>
          <li><strong>&lt;div&gt;</strong> – Generic container for grouping elements.</li>
        </ul>
      </section>

      {/* 3. Example Layout */}
      <section id="example">
        <h2>3. Example of a Page Layout</h2>
        <pre>
          <code>{`<body>
  <header>
      <h1>Website Title</h1>
      <nav>
          <a href="#">Home</a>
          <a href="#">About</a>
          <a href="#">Contact</a>
      </nav>
  </header>

  <main>
      <section>
          <h2>Section Title</h2>
          <p>Content goes here...</p>
      </section>
      <aside>
          <h3>Sidebar</h3>
          <p>Additional info or links.</p>
      </aside>
  </main>

  <footer>
      <p>&copy; 2024 Your Company</p>
  </footer>
</body>`}</code>
        </pre>
      </section>

      {/* 4. Layout Techniques */}
      <section id="layout-techniques">
        <h2>4. Layout Techniques</h2>
        <ul>
          <li><strong>Block Elements:</strong> Stack vertically by default (<code>&lt;div&gt;</code>, <code>&lt;section&gt;</code>).</li>
          <li><strong>Flexbox:</strong> Use <code>display: flex;</code> for horizontal or vertical alignment.</li>
          <li><strong>CSS Grid:</strong> Define rows and columns for complex layouts.</li>
          <li><strong>Positioning:</strong> Use <code>static</code>, <code>relative</code>, <code>absolute</code>, <code>fixed</code>, or <code>sticky</code> for precise placement.</li>
          <li><strong>Responsive Design:</strong> Use media queries and relative units to adjust layout for different screens.</li>
        </ul>
      </section>

      {/* 5. Semantic vs Non-Semantic Layout */}
      <section id="semantic-vs-non-semantic">
        <h2>5. Semantic vs Non-Semantic Layout</h2>
        <p>
          <strong>Semantic Elements:</strong> <code>&lt;header&gt;</code>, <code>&lt;nav&gt;</code>, <code>&lt;main&gt;</code>, <code>&lt;footer&gt;</code> – provide meaning and improve accessibility and SEO.
        </p>
        <p>
          <strong>Non-Semantic Elements:</strong> <code>&lt;div&gt;</code>, <code>&lt;span&gt;</code> – generic containers with no inherent meaning, mainly used for styling.
        </p>
      </section>

      {/* 6. Responsive Layout Example */}
      <section id="responsive">
        <h2>6. Responsive Layout Example (Flexbox)</h2>
        <pre>
          <code>{`<style>
  .container {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
  }
  .container > div {
      flex: 1 1 200px;
      padding: 1rem;
      border: 1px solid #ccc;
  }
</style>

<div class="container">
  <div>Main Content</div>
  <div>Sidebar</div>
  <div>Advertisement</div>
</div>`}</code>
        </pre>
      </section>

      {/* 7. Summary */}
      <section id="summary">
        <h2>7. Summary</h2>
        <ul>
          <li>HTML layout defines the structure of a webpage.</li>
          <li>Use semantic elements like <code>&lt;header&gt;</code>, <code>&lt;nav&gt;</code>, <code>&lt;main&gt;</code>, <code>&lt;section&gt;</code>, <code>&lt;article&gt;</code>, <code>&lt;aside&gt;</code>, <code>&lt;footer&gt;</code>.</li>
          <li>Use CSS Flexbox or Grid for modern, responsive layouts.</li>
          <li>Non-semantic elements like <code>&lt;div&gt;</code> can be used for styling and grouping.</li>
          <li>Responsive design ensures your layout works across devices.</li>
        </ul>
      </section>
    </div>
  );
};

export default HTMLLayout;
